import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { UserPlus, X, Phone, Bike, MapPin, Trash2, Pencil, History, FileDown, ChevronRight, Truck, Trophy, Medal, TrendingUp } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/orderStatus";
import PhoneVerify from "@/components/PhoneVerify";

const MEDAL_CLS = ["bg-[#FFBA08] text-[#1A1A1A]", "bg-[#C0C0C0] text-[#1A1A1A]", "bg-[#CD7F32] text-white"];
const medalClass = (i) => MEDAL_CLS[i] || "bg-white/15 text-white";

const TRANSPORTS = ["Moto", "Tricycle", "Voiture", "Vélo", "À pied"];

export default function Drivers() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(null); // driver object or {} for new
  const [history, setHistory] = useState(null); // driver for history view

  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });
  const [period, setPeriod] = useState("month");
  const { data: lb } = useQuery({ queryKey: ["drivers-leaderboard", period], queryFn: async () => (await api.get(`/drivers/leaderboard?period=${period}`)).data });
  const ranking = lb?.drivers || [];

  const del = async (d) => {
    if (!window.confirm(t("driversp.confirmDelete", { name: d.first_name }))) return;
    await api.delete(`/drivers/${d.driver_id}`);
    qc.invalidateQueries({ queryKey: ["drivers"] });
    toast.success(t("driversp.deleted"));
  };

  return (
    <div className="px-4 pt-6">
      <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1">{t("driversp.title")}</h1>
      <p className="text-sm text-[#5C574F] mb-4">{t("driversp.sub")}</p>

      {ranking.length > 0 && (
        <div className="bg-[#1B4332] text-white rounded-2xl p-4 mb-4" data-testid="leaderboard">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold flex items-center gap-2"><Trophy className="h-5 w-5 text-[#FFBA08]" /> {t("driversp.leaderboard")}</h2>
            <div className="flex bg-white/10 rounded-full p-0.5 text-xs">
              <button data-testid="lb-period-month" onClick={() => setPeriod("month")} className={`px-3 py-1 rounded-full font-semibold transition-colors ${period === "month" ? "bg-white text-[#1B4332]" : "text-white/80"}`}>{t("driversp.thisMonth")}</button>
              <button data-testid="lb-period-all" onClick={() => setPeriod("all")} className={`px-3 py-1 rounded-full font-semibold transition-colors ${period === "all" ? "bg-white text-[#1B4332]" : "text-white/80"}`}>{t("driversp.all")}</button>
            </div>
          </div>
          <div className="space-y-2">
            {ranking.slice(0, 5).map((r, i) => (
              <div key={r.driver_id} className="flex items-center gap-3 bg-white/8 rounded-xl p-2.5" data-testid={`lb-row-${r.driver_id}`}>
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${medalClass(i)}`}>
                  {i < 3 ? <Medal className="h-4 w-4" /> : i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate">{r.name}</p>
                  <div className="h-1.5 bg-white/15 rounded-full mt-1 overflow-hidden"><div className="h-full bg-[#FFBA08]" style={{ width: `${r.success_rate}%` }} /></div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-bold text-sm">{r.delivered} <span className="text-white/60 text-xs font-normal">{t("driversp.deliv")}</span></p>
                  <p className="text-[10px] text-[#FFBA08] font-semibold">{t("driversp.successRate", { rate: r.success_rate })}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/60 mt-3 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {t("driversp.collectedLabel", { period: period === "month" ? t("driversp.collectedThis") : t("driversp.collectedAll"), amount: money(ranking.reduce((s, r) => s + (r.collected || 0), 0), ranking[0]?.currency || "XOF") })}</p>
        </div>
      )}

      <button data-testid="add-driver-btn" onClick={() => setEditing({})} className="w-full h-14 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 mb-4 active:scale-95 transition-transform">
        <UserPlus className="h-5 w-5" /> {t("driversp.addDriver")}
      </button>

      {drivers.length === 0 && (
        <div className="text-center py-14 text-[#9c968a]">
          <Truck className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t("driversp.noDrivers")}</p>
        </div>
      )}

      <div className="space-y-3">
        {drivers.map((d) => (
          <div key={d.driver_id} className="bg-white rounded-2xl border border-black/5 shadow-sm p-4" data-testid={`driver-${d.driver_id}`}>
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-full bg-[#E85D04]/10 text-[#E85D04] flex items-center justify-center font-display font-bold shrink-0">{(d.first_name?.[0] || "?").toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold leading-tight">{d.first_name} {d.last_name}</p>
                <a href={`tel:${d.phone}`} className="text-sm text-[#0077B6] flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {d.phone}</a>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[#9c968a]">
                  {d.matricule && <span>{t("driversp.mat")} {d.matricule}</span>}
                  {d.transport && <span className="flex items-center gap-1"><Bike className="h-3 w-3" /> {d.transport}</span>}
                  {d.zone && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {d.zone}</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`edit-driver-${d.driver_id}`} onClick={() => setEditing(d)} className="h-9 w-9 rounded-lg bg-[#F9F6F0] text-[#5C574F] flex items-center justify-center"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-driver-${d.driver_id}`} onClick={() => del(d)} className="h-9 w-9 rounded-lg bg-[#D00000]/10 text-[#D00000] flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <button data-testid={`history-driver-${d.driver_id}`} onClick={() => setHistory(d)} className="w-full h-10 mt-3 rounded-xl bg-[#F9F6F0] text-[#5C574F] font-semibold text-sm flex items-center justify-center gap-2">
              <History className="h-4 w-4" /> {t("driversp.tourHistory")} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editing && <DriverForm driver={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["drivers"] }); }} />}
        {history && <DriverHistory driver={history} onClose={() => setHistory(null)} />}
      </AnimatePresence>
    </div>
  );
}

function DriverForm({ driver, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!driver.driver_id;
  const [f, setF] = useState({ first_name: driver.first_name || "", last_name: driver.last_name || "", matricule: driver.matricule || "", phone: driver.phone || "", transport: driver.transport || "", zone: driver.zone || "" });
  const [busy, setBusy] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(!!driver.phone_verified);

  const save = async () => {
    if (!f.first_name || !f.phone) { toast.error(t("driversp.firstNamePhoneReq")); return; }
    if (!phoneVerified) { toast.error(t("driversp.verifyDriverPhone")); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/drivers/${driver.driver_id}`, f);
      else await api.post("/drivers", f);
      toast.success(isEdit ? t("driversp.updated") : t("driversp.added"));
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const transportLabels = { "Moto": t("driversp.tMoto"), "Tricycle": t("driversp.tTricycle"), "Voiture": t("driversp.tVoiture"), "Vélo": t("driversp.tVelo"), "À pied": t("driversp.tPied") };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="driver-form-modal">
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-extrabold">{isEdit ? t("driversp.editDriver") : t("driversp.newDriver")}</h3>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="driver-firstname" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} placeholder={t("driversp.firstName")} className="h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" />
            <input data-testid="driver-lastname" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} placeholder={t("driversp.lastName")} className="h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" />
          </div>
          <input data-testid="driver-phone" value={f.phone} onChange={(e) => { setF({ ...f, phone: e.target.value }); setPhoneVerified(false); }} placeholder={t("driversp.phoneWa")} className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" />
          <PhoneVerify phone={f.phone} verified={phoneVerified} onVerified={() => setPhoneVerified(true)} hint={t("driversp.driverPhoneHint")} />
          <input data-testid="driver-matricule" value={f.matricule} onChange={(e) => setF({ ...f, matricule: e.target.value })} placeholder={t("driversp.matricule")} className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" />
          <select data-testid="driver-transport" value={f.transport} onChange={(e) => setF({ ...f, transport: e.target.value })} className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]">
            <option value="">{t("driversp.transportMode")}</option>
            {TRANSPORTS.map((tr) => <option key={tr} value={tr}>{transportLabels[tr] || tr}</option>)}
          </select>
          <input data-testid="driver-zone" value={f.zone} onChange={(e) => setF({ ...f, zone: e.target.value })} placeholder={t("driversp.zone")} className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#E85D04]" />
        </div>
        <button data-testid="save-driver-btn" onClick={save} disabled={busy} className="w-full h-14 mt-5 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60">{busy ? t("common.saving") : t("common.save")}</button>
      </motion.div>
    </div>
  );
}

function DriverHistory({ driver, onClose }) {
  const { t } = useTranslation();
  const { data: tours = [], isLoading } = useQuery({ queryKey: ["driver-tours", driver.driver_id], queryFn: async () => (await api.get(`/drivers/${driver.driver_id}/tours`)).data });
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="driver-history-modal">
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-extrabold">{t("driversp.historyOf", { name: driver.first_name })}</h3>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-[#5C574F] mb-4">{t("driversp.reportsSub")}</p>
        {isLoading ? <p className="text-sm text-[#9c968a] py-6 text-center">{t("tracking.loading")}</p> : tours.length === 0 ? (
          <div className="text-center py-10 text-[#9c968a]">
            <History className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">{t("driversp.noTours")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tours.map((tr) => (
              <div key={tr.tour_id} className="bg-[#F9F6F0] rounded-2xl p-4" data-testid={`history-tour-${tr.tour_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm">{fmt(tr.completed_at)}</p>
                  <span className="text-xs font-bold text-[#2D6A4F]">{t("driversp.deliveredCount", { delivered: tr.delivered, total: tr.total })}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#9c968a] mb-3">
                  <span className="text-[#2D6A4F] font-semibold">✅ {t("driversp.success", { count: tr.delivered })}</span>
                  {tr.failed > 0 && <span className="text-[#D00000] font-semibold">❌ {t("driversp.failures", { count: tr.failed })}</span>}
                </div>
                <a href={tr.report_url} target="_blank" rel="noreferrer" data-testid={`history-pdf-${tr.tour_id}`} className="w-full h-10 rounded-xl bg-[#1B4332] text-white font-semibold text-sm flex items-center justify-center gap-2">
                  <FileDown className="h-4 w-4" /> {t("driversp.viewPdf")}
                </a>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
